import type {
  CheckoutDinnerVouchersOption,
  CheckoutDinnerVouchersService,
  ServiceMapper,
  ServiceType,
} from '../../../domain/types'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type { APIBookingDraft } from '@/scopes/checkout/infrastructure/api/types'
import type {
  APIBookingDraftDinnerVouchersService,
  APIBookingDraftDinnerVouchersServiceOption,
} from '@/scopes/checkout/infrastructure/api/types/services/dinnerVouchers'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultGA4ImpressionItems,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceOptionMapper?: ServiceOptionMapper
  serviceBannersMapper?: ServiceBannersMapper
  serviceType: ServiceType
}

export const createDinnerVouchersServiceMapper: (
  dependencies: Dependencies,
) => ServiceMapper<CheckoutDinnerVouchersService> = (dependencies) => {
  const {
    serviceName,
    serviceType,
    serviceTypeMapper,
    serviceOptionMapper = createServiceOptionMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
  } = dependencies

  function map(bookingDraft: APIBookingDraft): CheckoutDinnerVouchersService | null {
    const service = bookingDraft.services.find(
      ({ aName }) => aName === serviceName,
    ) as APIBookingDraftDinnerVouchersService | undefined

    if (!service)
      return null

    const { aName, options } = service
    const selectedOption = options.find(o => o.isSelected && !o.isNoneOption)

    return {
      id: aName,
      isSelected: selectedOption !== undefined,
      type: serviceType,
      selectedValue: selectedOption?.code ?? null,
      selectedQuantity: selectedOption?.quantity ?? 0,
      options: options
        .filter(o => !o.isNoneOption)
        .map(option => mapOption(option, service, bookingDraft)),
      banners: serviceBannersMapper.map(service),
      isPricePerPerson: service.isPricePerPerson ?? false,
    }
  }

  function mapOption(
    option: APIBookingDraftDinnerVouchersServiceOption,
    service: APIBookingDraftDinnerVouchersService,
    bookingDraft: APIBookingDraft,
  ): CheckoutDinnerVouchersOption {
    const baseOption = serviceOptionMapper.map(option, service, bookingDraft)

    return {
      ...baseOption,
      quantity: option.quantity ?? 1,
      startDate: option.startDate,
      endDate: option.endDate,
      appliesToParticipants: option.appliesToParticipants.map(id => id.toString()),
    }
  }

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function mapSelection(service: CheckoutDinnerVouchersService) {
    return [
      {
        value: service.selectedValue,
        quantity: service.selectedQuantity,
      },
    ]
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName)
    if (!service)
      return []
    return mapDefaultGA4ImpressionItems(service, bookingDraft, serviceOptionMapper, config, serviceType)
  }

  function mapSelectedItemToGA4(service: CheckoutDinnerVouchersService, step: number): ImpressionViewAncillariesItem[] {
    if (!service.selectedValue)
      return []

    const option = service.options.find(o => o.value === service.selectedValue)
    if (!option)
      return []

    const input: SelectedItemInput = {
      label: option.label,
      value: option.value,
      price: option.price,
      rawPrice: option.rawPrice,
      quantity: String(service.selectedQuantity || 1),
    }

    return mapDefaultSelectedItemToGA4([input], {
      serviceType,
      serviceId: service.id,
      step,
    })
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
